/**
 * The Help Desk's answer on a job (#1669, mockup screens 1–2): what's in the
 * way, in plain words, with a button when the office can fix it right here,
 * and "Checked N things · M look right" so the answer can be trusted.
 *
 * #1674 (mockup screen 3): what it can't fix goes to the owner in one tap —
 * after a fix that didn't work, or "Not what's wrong?" — with the job and the
 * check-up attached, so nobody explains it twice.
 *
 * Lives inside the top-bar Help Desk panel, so it uses the toolbar's own
 * classes (toolbarStyles.ts `.hck-*`), not Tailwind.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, CircleAlert } from "lucide-react";
import {
    useCheckupFix,
    useCheckupHandoff,
    useJobCheckup,
    type CheckupProblem,
    type FixResult,
    type HandoffResult,
} from "@/lib/use-job-checkup";

/** "the office lead has this one now" → "The office lead…" (no owner set). */
function capitalize(s: string): string {
    return s ? s[0]!.toUpperCase() + s.slice(1) : s;
}

type HandoffState =
    | null
    | { mode: "form"; problemTitle: string | null; fixResult: string | null }
    | { mode: "sent"; res: HandoffResult; urgent: boolean; hadMessage: boolean };

export function JobCheckupPanel({ jobId }: { jobId: string }) {
    const q = useJobCheckup(jobId);
    const fix = useCheckupFix(jobId);
    const [showChecks, setShowChecks] = useState(false);
    /** The last fix's outcome, keyed by the problem it answered. */
    const [result, setResult] = useState<{ problemId: string; res: FixResult } | null>(null);
    const [runningId, setRunningId] = useState<string | null>(null);
    const handoffM = useCheckupHandoff(jobId);
    const [handoff, setHandoff] = useState<HandoffState>(null);
    const [message, setMessage] = useState("");
    const [handoffError, setHandoffError] = useState<string | null>(null);

    if (q.isLoading) return <div className="hb gw">Checking this job…</div>;
    if (q.isError || !q.data) {
        return (
            <div className="hb gw">
                I couldn&apos;t check this job just now, so I won&apos;t guess. Tell me what&apos;s wrong below.
            </div>
        );
    }
    const { problems, checks } = q.data;
    const okCount = checks.filter((c) => c.ok).length;
    const who = q.data.handoffTo?.trim() || "the office lead";
    const failedProblem = result && !result.res.ok ? problems.find((p) => p.id === result.problemId) : undefined;

    const send = (urgent: boolean) => {
        if (!handoff || handoff.mode !== "form" || handoffM.isPending) return;
        const text = message.trim();
        if (!text && !handoff.problemTitle) {
            setHandoffError("Say what's wrong in a few words first.");
            return;
        }
        setHandoffError(null);
        handoffM.mutate(
            { message: text, urgent, problemTitle: handoff.problemTitle, fixResult: handoff.fixResult },
            {
                onSuccess: (res) => {
                    setHandoff({ mode: "sent", res, urgent, hadMessage: !!text });
                    setMessage("");
                },
                onError: (e) => setHandoffError(e.message || "That didn't send. Try again in a moment."),
            },
        );
    };

    if (handoff?.mode === "sent") {
        const name = handoff.res.handoffTo?.trim() || who;
        return (
            <div className="hck" data-testid="job-checkup-handoff-sent">
                <div className="hck-card">
                    <div className="hck-title">{capitalize(name)} has this one now</div>
                    <div className="hck-detail">I sent everything, so you won&apos;t have to explain it again:</div>
                    <ul className="hck-list">
                        <li data-ok="true">
                            <Check size={13} aria-hidden />
                            This job
                        </li>
                        {handoff.hadMessage && (
                            <li data-ok="true">
                                <Check size={13} aria-hidden />
                                What you told me
                            </li>
                        )}
                        <li data-ok="true">
                            <Check size={13} aria-hidden />
                            What I found when I checked it
                        </li>
                    </ul>
                    {handoff.urgent && <div className="hck-note">Marked urgent: a customer is waiting.</div>}
                </div>
                <ol className="hck-steps">
                    <li data-done="true">
                        <b>Sent to {name}</b> · just now
                    </li>
                    <li>{capitalize(name)} fixes it</li>
                    <li>You get a notification to check it&apos;s right</li>
                </ol>
                <button type="button" className="hck-link" onClick={() => setHandoff(null)}>
                    Back to this job
                </button>
            </div>
        );
    }

    const run = (p: CheckupProblem) => {
        if (!p.fix || fix.isPending) return;
        setRunningId(p.id);
        setResult(null);
        fix.mutate(p.fix, {
            onSuccess: (res) => setResult({ problemId: p.id, res }),
            onError: (e) =>
                setResult({
                    problemId: p.id,
                    res: {
                        ok: false,
                        message: e.message || "That didn't go through. Try again in a moment.",
                        checkup: null,
                    },
                }),
            onSettled: () => setRunningId(null),
        });
    };

    return (
        <div className="hck" data-testid="job-checkup-panel">
            {result && (
                <div className={`hck-result ${result.res.ok ? "ok" : "warn"}`} role="status">
                    {result.res.ok ? <Check size={16} aria-hidden /> : <CircleAlert size={16} aria-hidden />}
                    <span>{result.res.message}</span>
                </div>
            )}
            {failedProblem && handoff === null && (
                <button
                    type="button"
                    className="hck-fix"
                    onClick={() =>
                        setHandoff({ mode: "form", problemTitle: failedProblem.title, fixResult: result!.res.message })
                    }
                >
                    Send this to {who}
                </button>
            )}

            {problems.length === 0 ? (
                checks.length === 0 ? (
                    // #1701 — the 2026-10-01 test drive: "everything that has to be
                    // there is there" on a job where nothing had been checked.
                    <div className="hb gw">
                        There&apos;s nothing on this job for me to check right now: no tests, quotes or packet waiting.
                        If something looks wrong, tell me below.
                    </div>
                ) : (
                    <div className="hb gw">
                        I checked this job and everything that has to be there is there.
                        {q.data.held ? "" : " Nothing is holding it up."}
                    </div>
                )
            ) : (
                <>
                    <div className="hb gw">
                        I looked at this job.{" "}
                        <b>
                            {problems.length === 1
                                ? "One thing is in the way:"
                                : `${problems.length} things are in the way:`}
                        </b>
                    </div>
                    {problems.map((p) => (
                        <div key={p.id} className="hck-card" data-severity={p.severity}>
                            <div className="hck-title">{p.title}</div>
                            <div className="hck-detail">{p.detail}</div>
                            {p.link && !p.fix && (
                                <Link className="hck-link hck-golink" to={p.link.href}>
                                    {p.link.label} →
                                </Link>
                            )}
                            {p.fix && (
                                <button
                                    type="button"
                                    className="hck-fix"
                                    disabled={fix.isPending}
                                    onClick={() => run(p)}
                                >
                                    {runningId === p.id ? "Working on it…" : p.fix.label}
                                </button>
                            )}
                        </div>
                    ))}
                    {q.data.held &&
                        !problems.some((p) => p.id === "held_ready" || p.id === "held_no_customer_email") && (
                            <div className="hck-note">
                                The packet was already approved. It goes to the customer by itself once these are fixed.
                            </div>
                        )}
                </>
            )}

            {checks.length > 0 && (
                <div className="hck-checked">
                    <Check size={14} aria-hidden className="ok" />
                    <span>
                        Checked {checks.length} thing{checks.length === 1 ? "" : "s"} on this job · {okCount} look
                        {okCount === 1 ? "s" : ""} right
                    </span>
                    <button
                        type="button"
                        className="hck-link"
                        aria-expanded={showChecks}
                        onClick={() => setShowChecks((v) => !v)}
                    >
                        {showChecks ? "Hide" : "What I checked"}
                    </button>
                </div>
            )}
            {handoff?.mode === "form" ? (
                <div className="hck-card" data-testid="job-checkup-handoff-form">
                    <label className="hck-title" htmlFor="hck-handoff-msg">
                        {handoff.problemTitle ? `Anything to add for ${who}?` : "What's wrong?"}
                    </label>
                    <textarea
                        id="hck-handoff-msg"
                        className="hck-text"
                        rows={3}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={handoff.problemTitle ? "Optional" : "Say it the way you'd tell a coworker"}
                    />
                    {handoffError && <div className="hck-note warn">{handoffError}</div>}
                    <div className="hck-row">
                        <button
                            type="button"
                            className="hck-fix"
                            disabled={handoffM.isPending}
                            onClick={() => send(false)}
                        >
                            {handoffM.isPending ? "Sending…" : `Send it to ${who}`}
                        </button>
                        <button
                            type="button"
                            className="hck-alt"
                            disabled={handoffM.isPending}
                            onClick={() => send(true)}
                        >
                            It&apos;s urgent: customer waiting
                        </button>
                    </div>
                    <button type="button" className="hck-link" onClick={() => setHandoff(null)}>
                        Cancel
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    className="hck-link hck-notit"
                    onClick={() => setHandoff({ mode: "form", problemTitle: null, fixResult: null })}
                >
                    Not what&apos;s wrong? Send it to {who}
                </button>
            )}

            {showChecks && (
                <ul className="hck-list">
                    {checks.map((c) => (
                        <li key={c.id} data-ok={c.ok}>
                            {c.ok ? <Check size={13} aria-hidden /> : <CircleAlert size={13} aria-hidden />}
                            {c.label}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
